import { t } from "@lingui/macro";
import {
  type ListingClientTaskFragment,
  type ListingInternalTaskFragment,
  StarsStarableTypeEnum,
  type TaskPriority,
  UpdateTaskPriorityDocument,
  type UpdateTaskPriorityMutation,
  type UpdateTaskPriorityMutationVariables,
} from "@src/__generated__/graphql";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { action, computed, makeObservable, observable } from "mobx";

export type TaskModelInit =
  | ListingInternalTaskFragment
  | ListingClientTaskFragment;
export class TaskModel {
  id: ListingInternalTaskFragment["id"];
  @observable.ref name: ListingInternalTaskFragment["name"];
  @observable.ref status: ListingInternalTaskFragment["status"];
  @observable.ref billable?: ListingInternalTaskFragment["billable"];
  // @observable.ref commentCount: ListingInternalTaskFragment["commentCount"];
  is_template: ListingInternalTaskFragment["is_template"];
  @observable.ref positions: ListingInternalTaskFragment["positions"];
  // @observable.ref
  // scopedCommentCount: ListingInternalTaskFragment["scopedCommentCount"];
  @observable.ref priority: ListingInternalTaskFragment["priority"];
  @observable.ref deadline: ListingInternalTaskFragment["deadline"];
  @observable.ref
  ourWorkBudgetItem: ListingInternalTaskFragment["ourWorkBudgetItem"];
  @observable.ref parent: ListingInternalTaskFragment["parent"];
  @observable.ref
  task_template_group_id: ListingInternalTaskFragment["task_template_group_id"];
  // @observable.ref
  // unreadCommentsCount: ListingInternalTaskFragment["unreadCommentsCount"];
  @observable.ref scopes: ListingInternalTaskFragment["scopes"];
  @observable.ref stats?: ListingInternalTaskFragment["stats"];
  @observable.ref subtasks: TaskModel[] = [];
  @observable.ref from: ListingInternalTaskFragment["from"];
  @observable.ref to: ListingInternalTaskFragment["to"];
  @observable.ref created_at: ListingInternalTaskFragment["created_at"];
  @observable.ref
  is_starred: ListingInternalTaskFragment["is_starred"];

  isOpen = new BooleanState(true);

  private priorityMutator = new MutationHelper<
    UpdateTaskPriorityMutation,
    UpdateTaskPriorityMutationVariables
  >(UpdateTaskPriorityDocument);

  constructor(
    task: TaskModelInit,
    private appStore: AppStore,
  ) {
    makeObservable(this);
    this.id = task.id;
    this.name = task.name;
    this.status = task.status;
    this.billable = "billable" in task ? task.billable : undefined;
    // this.commentCount = "commentCount" in task ? task.commentCount : 0;
    this.is_template = task.is_template;
    this.positions = task.positions.map((position) => ({
      ...position,
      spent_time: "spent_time" in position ? position.spent_time : 0,
    }));
    // this.scopedCommentCount = task.scopedCommentCount;
    this.priority = task.priority;
    this.from = task.from;
    this.to = task.to;
    this.created_at = task.created_at;
    this.deadline = task.deadline;
    this.scopes = "scopes" in task ? task.scopes : [];
    this.ourWorkBudgetItem = task.ourWorkBudgetItem
      ? {
          id: task.ourWorkBudgetItem.id,
          title:
            "title" in task.ourWorkBudgetItem &&
            typeof task.ourWorkBudgetItem.title === "string"
              ? task.ourWorkBudgetItem.title
              : "",
          project: {
            id: task.ourWorkBudgetItem.project.id,
            title: task.ourWorkBudgetItem.project.title,
            code: task.ourWorkBudgetItem.project.code,
            uses_budgetovac: task.ourWorkBudgetItem.project.uses_budgetovac,
            brand: task.ourWorkBudgetItem.project.brand,
          },
        }
      : undefined;
    this.parent = task.parent;
    this.task_template_group_id = task.task_template_group_id;
    // this.unreadCommentsCount =
    //   "unreadCommentsCount" in task ? task.unreadCommentsCount : 0;
    this.stats = "stats" in task ? task.stats : undefined;
    this.subtasks =
      task.subtasks?.map((subtask) => new TaskModel(subtask, appStore)) ?? [];
    this.is_starred = task.is_starred;
  }

  @computed get isSubtask() {
    return !!this.parent?.id;
  }

  @computed get subtaskIds() {
    return this.subtasks.map(({ id }) => id);
  }

  @computed get showNonBillableIcon() {
    return this.billable === false;
  }

  @computed get showSubtasksToggle() {
    return this.subtasks.length > 0;
  }

  @computed get projectId() {
    return this.ourWorkBudgetItem?.project.id;
  }

  @computed get projectLabel() {
    if (!this.ourWorkBudgetItem) return t`Unassigned`;
    return `${this.ourWorkBudgetItem?.project.brand.client.name} - ${this.ourWorkBudgetItem?.project.brand.name} - ${this.ourWorkBudgetItem?.project.title}`;
  }

  @action.bound update(task: Partial<ListingInternalTaskFragment>) {
    Object.assign(this, task);
  }
  @action.bound removeSubtask(subtaskId: TaskModel["id"]) {
    this.subtasks = this.subtasks.filter(({ id }) => id !== subtaskId);
  }

  @computed get isPriorityUpdating() {
    return this.priorityMutator.isLoading.value;
  }

  @action.bound async updatePriority(priorityId: TaskPriority["id"]) {
    const [data, error] = await this.priorityMutator.mutate({
      task_id: this.id,
      priority_id: priorityId,
    });

    if (error) return;

    this.priority = data?.updateTaskPriority.priority;
  }

  @action.bound addSubtask(subtask: TaskModel) {
    this.subtasks = this.subtasks.concat(subtask);
  }

  @action.bound async createSubtask() {
    this.appStore.taskFormModalStore.modalState.onOpen({
      billable: this.billable,
      projectId: this.projectId,
      budgetItemId: this.ourWorkBudgetItem?.id,
      templateGroupId: this.task_template_group_id ?? undefined,
      isTemplate: this.is_template,
      filterParentTasksForGroup: Boolean(this.task_template_group_id),
      parentTask: this,
      is_starred: false,
      onSubmit: (subtask) => {
        this.addSubtask(new TaskModel(subtask, this.appStore));
        this.appStore.tasksListingStore.onAddSubtask(subtask.id);
      },
    });
  }

  @computed
  get isStarUpdating() {
    return (id: string) =>
      this.appStore.starStore.isLoading(id, StarsStarableTypeEnum.Task);
  }

  @action.bound async toggleStar() {
    if (this.is_starred) {
      const [_, error] = await this.appStore.starStore.unstar(
        this.id,
        StarsStarableTypeEnum.Task,
      );

      if (error) return;

      this.is_starred = false;

      this.appStore.UIStore.toast({
        title: t`Task unstarred`,
        status: "success",
      });

      return;
    }

    const [_, error] = await this.appStore.starStore.star(
      this.id,
      StarsStarableTypeEnum.Task,
    );

    if (error) return;

    this.is_starred = true;

    this.appStore.UIStore.toast({
      title: t`Task starred`,
      status: "success",
    });
  }
}
